import React, { useState } from 'react';
import { CheckCircle, XCircle, HelpCircle, Loader2, ShieldCheck } from 'lucide-react';
import { withBackend } from '../utils/api';
import { toast } from 'sonner';

const VERDICT_STYLES = {
  verified: {
    icon: CheckCircle,
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
    label: 'Verified',
  },
  contradicted: {
    icon: XCircle,
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-500/10',
    border: 'border-red-500/20',
    label: 'Contradicted',
  },
  unverified: {
    icon: HelpCircle,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    label: 'Unverified',
  },
};

export default function DriveFactVerifier({ slides, userId }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const slidesText = slides
    .filter(Boolean)
    .map(s => `${s.title || ''}\n${s.content || ''}`)
    .join('\n\n');

  const handleVerify = async () => {
    if (!slidesText.trim()) {
      toast.error('No slide content to verify');
      return;
    }
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const resp = await fetch(withBackend('/api/drive/verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, slides_text: slidesText }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Verification failed');
      setResults(data.results || []);
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const counts = results
    ? {
        verified: results.filter(r => r.verdict === 'verified').length,
        contradicted: results.filter(r => r.verdict === 'contradicted').length,
        unverified: results.filter(r => r.verdict === 'unverified').length,
      }
    : null;

  return (
    <div className="flex flex-col h-full p-3 space-y-3">
      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Fact Verifier</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Check your presentation's facts against files in your connected Drive.
        </p>
      </div>

      {/* Verify button */}
      <button
        onClick={handleVerify}
        disabled={loading || !slidesText.trim()}
        className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Verifying...
          </>
        ) : (
          <>
            <ShieldCheck className="w-4 h-4" />
            Verify Facts
          </>
        )}
      </button>

      {/* Summary counts */}
      {counts && (
        <div className="grid grid-cols-3 gap-1.5">
          {Object.entries(counts).map(([key, count]) => {
            const style = VERDICT_STYLES[key];
            return (
              <div key={key} className={`flex flex-col items-center py-2 rounded-lg border ${style.bg} ${style.border}`}>
                <span className={`text-lg font-bold ${style.color}`}>{count}</span>
                <span className={`text-[10px] font-medium ${style.color}`}>{style.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Results list */}
      {results && results.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-4">
          No verifiable claims found in slides.
        </p>
      )}

      {results && results.length > 0 && (
        <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
          {results.map((r, i) => {
            const style = VERDICT_STYLES[r.verdict] || VERDICT_STYLES.unverified;
            const Icon = style.icon;
            return (
              <div key={i} className={`p-2.5 rounded-lg border ${style.bg} ${style.border} space-y-1`}>
                <div className="flex items-start gap-1.5">
                  <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${style.color}`} />
                  <span className="text-xs font-medium leading-snug">{r.claim}</span>
                </div>
                {r.reason && (
                  <p className="text-[11px] text-muted-foreground pl-5">{r.reason}</p>
                )}
                {r.source && (
                  <p className="text-[10px] text-muted-foreground pl-5 italic">Source: {r.source}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {error && (
        <p className="text-xs text-destructive text-center">{error}</p>
      )}

      {!results && !loading && (
        <p className="text-xs text-muted-foreground text-center py-6 leading-relaxed">
          Connect Google Drive or OneDrive from the sidebar, then click Verify Facts to cross-check your presentation.
        </p>
      )}
    </div>
  );
}
